/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Badge,
} from "@mui/material"
import {
  Policy,
  Image as ImageIcon,
  CheckCircle,
  Warning,
  Save,
  ZoomIn,
  ZoomOut,
  Refresh,
  ArrowForwardIos,
  Public,
  CloudOff,
  CloudDone,
  Sync,
} from "@mui/icons-material"
import Swal from "sweetalert2"
import localforage from "localforage"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema táctico War Room
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const AuditoriaActas = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  const [actasNovedad, setActasNovedad] = useState([])
  const [actaActiva, setActaActiva] = useState(null)
  const [auditoriasEnCola, setAuditoriasEnCola] = useState([])

  // Estados de RLS
  const [esGlobal, setEsGlobal] = useState(false)
  const [cobertura, setCobertura] = useState("Cargando...")
  const [errorAcceso, setErrorAcceso] = useState(null)

  // Estados para la corrección
  const [votosBlancos, setVotosBlancos] = useState("")
  const [votosNulos, setVotosNulos] = useState("")
  const [votosCandidatos, setVotosCandidatos] = useState({})
  const [zoomImagen, setZoomImagen] = useState(1)

  // ========================================================
  // 1. MONITOREO DE RED Y CARGA INICIAL
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    verificarColaLocal()
    cargarActasConNovedad()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("auditoria_offline_queue")) || []
    setAuditoriasEnCola(cola)
  }

  const cargarActasConNovedad = async () => {
    setIsLoading(true)
    setActaActiva(null)
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getActasAuditoria", {})
      const data = res?.data !== undefined ? res.data : res

      if (data && data.success === false) {
        setErrorAcceso(data.message)
        return
      }

      setActasNovedad(data.data || [])
      setEsGlobal(data.es_global)
      setCobertura(data.cobertura)

      await localforage.setItem("auditoria_bandeja_cache", data.data || [])
      await localforage.setItem("auditoria_global_cache", data.es_global)
      await localforage.setItem("auditoria_cobertura_cache", data.cobertura)
    } catch (error) {
      console.log("Cargando auditoría desde caché offline...")
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const cache = (await localforage.getItem("auditoria_bandeja_cache")) || []
        const gCache = (await localforage.getItem("auditoria_global_cache")) || false
        const cCache = (await localforage.getItem("auditoria_cobertura_cache")) || "Caché Offline"
        setActasNovedad(cache)
        setEsGlobal(gCache)
        setCobertura(cCache)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ========================================================
  // 2. SELECCIÓN Y PREPARACIÓN DE CORRECCIÓN
  // ========================================================
  const seleccionarActa = (acta) => {
    setActaActiva(acta)
    setVotosBlancos(acta.blancos_orig.toString())
    setVotosNulos(acta.nulos_orig.toString())

    const objVotos = {}
    acta.votos_orig.forEach((v) => {
      objVotos[v.cand_codigo] = v.cantidad.toString()
    })
    setVotosCandidatos(objVotos)
    setZoomImagen(1)
  }

  const handleVotoChange = (cand_codigo, valor) => {
    const soloNumeros = valor.replace(/\D/g, "")
    setVotosCandidatos((prev) => ({ ...prev, [cand_codigo]: soloNumeros }))
  }

  const totalCandidatos = actaActiva
    ? actaActiva.votos_orig.reduce((acc, cand) => acc + (parseInt(votosCandidatos[cand.cand_codigo]) || 0), 0)
    : 0
  const totalGeneral = totalCandidatos + (parseInt(votosBlancos) || 0) + (parseInt(votosNulos) || 0)
  const cuadreCorrecto = actaActiva ? totalGeneral <= actaActiva.padron : false

  // ========================================================
  // 3. GUARDADO DE LA AUDITORÍA (ONLINE / OFFLINE)
  // ========================================================
  const handleGuardarAuditoria = async () => {
    if (!cuadreCorrecto) {
      return Swal.fire(
        "Inconsistencia",
        "La suma de votos sigue siendo mayor al padrón. Revise la fotografía de nuevo.",
        "error",
      )
    }

    const confirmacion = await Swal.fire({
      title: "¿Confirmar Corrección?",
      text: "Esta acción sobrescribirá los datos numéricos y pasará el acta al escrutinio oficial.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#196C87",
      cancelButtonColor: "#d32f2f",
      confirmButtonText: "Sí, Validar Acta",
    })

    if (!confirmacion.isConfirmed) return

    const payload = {
      acta_codigo: actaActiva.acta_codigo,
      blancos_corregidos: parseInt(votosBlancos) || 0,
      nulos_corregidos: parseInt(votosNulos) || 0,
      votos_corregidos: actaActiva.votos_orig.map((c) => ({
        cand_codigo: c.cand_codigo,
        cantidad: parseInt(votosCandidatos[c.cand_codigo]) || 0,
      })),
    }

    if (isOffline) {
      // Guardado local
      const cola = (await localforage.getItem("auditoria_offline_queue")) || []
      cola.push(payload)
      await localforage.setItem("auditoria_offline_queue", cola)
      setAuditoriasEnCola(cola)

      const actasActualizadas = actasNovedad.filter((a) => a.acta_codigo !== actaActiva.acta_codigo)
      setActasNovedad(actasActualizadas)
      await localforage.setItem("auditoria_bandeja_cache", actasActualizadas)

      setActaActiva(null)
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "info",
        title: "Guardado Offline",
        showConfirmButton: false,
        timer: 2500,
      })
    } else {
      // Envío real
      try {
        Swal.fire({ title: "Procesando...", allowOutsideClick: false, didOpen: () => Swal.showLoading() })
        const res = await api.post("/ControlElectoral/guardarAuditoriaActa", payload)

        if (res.data?.success || res.success) {
          Swal.fire("Acta Cuadrada", "El acta ha sido sumada al escrutinio oficial.", "success")
          const actasActualizadas = actasNovedad.filter((a) => a.acta_codigo !== actaActiva.acta_codigo)
          setActasNovedad(actasActualizadas)
          await localforage.setItem("auditoria_bandeja_cache", actasActualizadas)
          setActaActiva(null)
        } else {
          throw new Error("Respuesta fallida del servidor")
        }
      } catch (error) {
        const cola = (await localforage.getItem("auditoria_offline_queue")) || []
        cola.push(payload)
        await localforage.setItem("auditoria_offline_queue", cola)
        setAuditoriasEnCola(cola)
        setActasNovedad((prev) => prev.filter((a) => a.acta_codigo !== actaActiva.acta_codigo))
        setActaActiva(null)
        Swal.fire({ icon: "warning", title: "Red Inestable", text: "La validación se encoló localmente." })
      }
    }
  }

  // ========================================================
  // 4. SINCRONIZACIÓN MANUAL (PWA)
  // ========================================================
  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Aún sin red", "Busque conexión para transmitir validaciones.", "warning")
    if (auditoriasEnCola.length === 0) return

    setIsSyncing(true)
    let colaRestante = [...auditoriasEnCola]

    for (let i = 0; i < auditoriasEnCola.length; i++) {
      try {
        await api.post("/ControlElectoral/guardarAuditoriaActa", auditoriasEnCola[i])
        colaRestante = colaRestante.filter((c) => c.acta_codigo !== auditoriasEnCola[i].acta_codigo)
      } catch (err) {
        console.error("Fallo al subir acta auditada", err)
      }
    }

    await localforage.setItem("auditoria_offline_queue", colaRestante)
    setAuditoriasEnCola(colaRestante)
    setIsSyncing(false)

    if (colaRestante.length === 0) {
      Swal.fire("Sincronizado", "Todas las actas auditadas subieron con éxito.", "success")
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-3" style={{ backgroundColor: "#eef2f6", minHeight: "100vh" }}>
        <BackIcon />

        {/* TOPBAR ESTADO Y RED */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? (
              <CloudOff color="error" sx={{ fontSize: 32, mr: 1 }} />
            ) : (
              <CloudDone color="success" sx={{ fontSize: 32, mr: 1 }} />
            )}
            <Typography variant="subtitle1" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "MODO OFFLINE ACTIVADO" : "CONEXIÓN ESTABLE"}
            </Typography>
          </Box>
          <Badge badgeContent={auditoriasEnCola.length} color="secondary">
            <IconButton
              onClick={sincronizarCola}
              disabled={auditoriasEnCola.length === 0 || isOffline || isSyncing}
              sx={{ bgcolor: "#e3f2fd" }}
            >
              <Sync color={auditoriasEnCola.length > 0 ? "primary" : "disabled"} />
            </IconButton>
          </Badge>
        </Box>

        {/* ENCABEZADO */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight="900" color="error.main" display="flex" alignItems="center">
              <Policy sx={{ fontSize: 40, mr: 1 }} /> Auditoría de Actas
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
              Resolución de inconsistencias numéricas y validación OCR
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={cargarActasConNovedad}
            disabled={isLoading || isOffline}
            sx={{ bgcolor: "white", fontWeight: "bold", display: { xs: "none", sm: "flex" } }}
          >
            Refrescar Bandeja
          </Button>
        </Box>

        {/* BANNERS DE COBERTURA (RLS) E INCONVENIENTES */}
        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            <Box display="flex" alignItems="center" mb={3} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight="bold" display="flex" alignItems="center">
                📍 Cobertura: {cobertura}
              </Typography>
              {esGlobal && (
                <Chip
                  icon={<Public fontSize="small" />}
                  label="MODO GLOBAL"
                  color="info"
                  size="small"
                  sx={{ ml: 2, fontWeight: "bold" }}
                />
              )}
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" my={10}>
                <CircularProgress size={60} color="error" />
              </Box>
            ) : actasNovedad.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 10,
                  textAlign: "center",
                  bgcolor: "transparent",
                  border: "2px dashed #a5d6a7",
                  borderRadius: 3,
                }}
              >
                <CheckCircle sx={{ fontSize: 80, color: "#2E7D32", mb: 2 }} />
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  Bandeja Limpia
                </Typography>
                <Typography variant="h6" color="textSecondary">
                  No hay actas con inconsistencias en este momento.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {/* COLUMNA IZQUIERDA: BANDEJA DE ENTRADA */}
                <Grid item xs={12} md={3}>
                  <Paper
                    elevation={3}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      bgcolor="#d32f2f"
                      color="white"
                      p={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="h6" fontWeight="bold">
                        Pendientes
                      </Typography>
                      <Chip
                        label={actasNovedad.length}
                        size="small"
                        sx={{ bgcolor: "white", color: "#d32f2f", fontWeight: "bold" }}
                      />
                    </Box>
                    <Box sx={{ overflowY: "auto", flexGrow: 1, p: 1 }}>
                      {actasNovedad.map((acta) => (
                        <Card
                          key={acta.acta_codigo}
                          elevation={actaActiva?.acta_codigo === acta.acta_codigo ? 4 : 1}
                          sx={{
                            mb: 2,
                            cursor: "pointer",
                            borderLeft: "6px solid #d32f2f",
                            bgcolor: actaActiva?.acta_codigo === acta.acta_codigo ? "#ffebee" : "white",
                          }}
                          onClick={() => seleccionarActa(acta)}
                        >
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="textPrimary">
                              {acta.acta_codigo}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                              {acta.junta_nombre}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="primary.main"
                              display="block"
                              mb={1}
                              fontStyle="italic"
                            >
                              Registró: {acta.del_nombre}
                            </Typography>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Chip
                                size="small"
                                icon={<Warning />}
                                label="No cuadra"
                                color="error"
                                variant="outlined"
                              />
                              <ArrowForwardIos sx={{ fontSize: 14, color: "action.active" }} />
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                {/* ZONA DE TRABAJO (SPLIT SCREEN) */}
                {actaActiva ? (
                  <Grid item xs={12} md={9}>
                    <Grid container spacing={3} height="100%">
                      {/* VISOR DE IMAGEN (IZQUIERDA) */}
                      <Grid item xs={12} md={7}>
                        <Paper
                          elevation={3}
                          sx={{ p: 2, borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              <ImageIcon sx={{ mr: 1, verticalAlign: "bottom" }} /> Evidencia Fotográfica
                            </Typography>
                            <Box>
                              <IconButton onClick={() => setZoomImagen((prev) => Math.max(0.5, prev - 0.2))}>
                                <ZoomOut />
                              </IconButton>
                              <Typography variant="caption" fontWeight="bold">
                                {Math.round(zoomImagen * 100)}%
                              </Typography>
                              <IconButton onClick={() => setZoomImagen((prev) => Math.min(3, prev + 0.2))}>
                                <ZoomIn />
                              </IconButton>
                            </Box>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <Box
                            sx={{
                              flexGrow: 1,
                              overflow: "auto",
                              bgcolor: "#eeeeee",
                              borderRadius: 2,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "flex-start",
                              p: 2,
                            }}
                          >
                            {/* La foto soporta URLs directas o base64 (data:image/...) */}
                            <img
                              src={actaActiva.foto_url}
                              alt="Evidencia del Acta"
                              style={{
                                transform: `scale(${zoomImagen})`,
                                transformOrigin: "top center",
                                transition: "transform 0.2s",
                                maxWidth: "100%",
                              }}
                            />
                          </Box>
                        </Paper>
                      </Grid>

                      {/* FORMULARIO DE CORRECCIÓN (DERECHA) */}
                      <Grid item xs={12} md={5}>
                        <Paper
                          elevation={3}
                          sx={{ p: 3, borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
                        >
                          <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>
                            Corrección Numérica
                          </Typography>
                          <Typography variant="body2" color="textSecondary" mb={2}>
                            Verifique la imagen y corrija el error de digitación del delegado.
                          </Typography>

                          <Box bgcolor="#e3f2fd" p={2} borderRadius={2} mb={3}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" mb={1}>
                              Votos Genéricos
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="BLANCOS"
                                  value={votosBlancos}
                                  onChange={(e) => setVotosBlancos(e.target.value.replace(/\D/g, ""))}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="NULOS"
                                  value={votosNulos}
                                  onChange={(e) => setVotosNulos(e.target.value.replace(/\D/g, ""))}
                                />
                              </Grid>
                            </Grid>
                          </Box>

                          <Typography variant="subtitle2" color="primary" fontWeight="bold" mb={1}>
                            Votos por Candidato
                          </Typography>
                          <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1, mb: 2 }}>
                            {actaActiva.votos_orig.map((cand) => (
                              <Box
                                key={cand.cand_codigo}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                                p={1}
                                borderBottom="1px solid #e0e0e0"
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  {cand.cand_nombre}
                                </Typography>
                                <TextField
                                  size="small"
                                  sx={{ width: "80px" }}
                                  value={votosCandidatos[cand.cand_codigo] || ""}
                                  onChange={(e) => handleVotoChange(cand.cand_codigo, e.target.value)}
                                  inputProps={{ style: { textAlign: "center", fontWeight: "bold" } }}
                                />
                              </Box>
                            ))}
                          </Box>

                          <Divider sx={{ mb: 2 }} />

                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Suma Total:
                            </Typography>
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              color={cuadreCorrecto ? "success.main" : "error.main"}
                            >
                              {totalGeneral}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="body2" color="textSecondary">
                              Padrón de Mesa:
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {actaActiva.padron}
                            </Typography>
                          </Box>

                          {!cuadreCorrecto && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              La suma de votos supera el padrón.
                            </Alert>
                          )}

                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            startIcon={<Save />}
                            disabled={!cuadreCorrecto}
                            onClick={handleGuardarAuditoria}
                            sx={{ py: 1.5, fontWeight: "bold" }}
                          >
                            VALIDAR Y CUADRAR ACTA
                          </Button>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid
                    item
                    xs={12}
                    md={9}
                    sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}
                  >
                    <Box textAlign="center" opacity={0.5}>
                      <Policy sx={{ fontSize: 80, mb: 2 }} />
                      <Typography variant="h5" fontWeight="bold">
                        Seleccione un acta
                      </Typography>
                      <Typography variant="body1">
                        Haga clic en un acta de la bandeja izquierda para auditarla.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default AuditoriaActas
